import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getSarList,
  addSarAccess,
  addSarAllAccess,
  revokeSarAccess,
  sarNotify
} from '../services/api';
import {
  SarRequest,
  SarAllAccessRequest,
  SarRevokeRequest,
  SarNotifyRequest
} from '../types';
import RegionCombobox from './RegionCombobox';
import './SarScreen.css';

interface SarScreenProps {
  onBack: () => void;
}

const SarScreen: React.FC<SarScreenProps> = ({ onBack }) => {
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<'individual' | 'all' | 'notify' | 'list'>('individual');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Individual SAR form
  const [individualForm, setIndividualForm] = useState({
    account_id: '',
    email: '',
    region: '',
    module: '',
  });

  // Notification form
  const [notifyForm, setNotifyForm] = useState({
    release_version: '',
    deployment_manual_url: 'https://docs.rainmaker.espressif.com/docs/deploy/upgrade-deployment/deployment-upgrades',
    release_notes_url: 'https://docs.rainmaker.espressif.com/docs/deploy/release-notes/backend-releases',
    email_list: 'all',
    custom_email: '',
    send_to_private: true,
    send_to_public: false,
  });

  // SAR list
  const [listForm, setListForm] = useState({
    account_id: '',
  });
  const [sarListData, setSarListData] = useState<string>('');

  const clearMessages = () => {
    setMessage('');
    setError('');
  };

  const handleIndividualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAuthenticated || !individualForm.account_id || !individualForm.email || !individualForm.region) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      clearMessages();

      const request: SarRequest = {
        account_id: individualForm.account_id.trim(),
        email: individualForm.email.trim(),
        region: individualForm.region.trim(),
        module: individualForm.module.trim() || undefined,
      };

      const response = await addSarAccess(request);

      if (response.data.status === 'success') {
        setMessage(`SAR access granted successfully for ${individualForm.email}`);
        setIndividualForm({ account_id: '', email: '', region: '', module: '' });
      } else {
        setError(response.data.description || 'Failed to add SAR access');
      }
    } catch (err: any) {
      const errorMsg = typeof err.response?.data?.description === 'string'
        ? err.response.data.description
        : 'Failed to add SAR access';
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleIndividualRevoke = async () => {
    if (!isAuthenticated) return;

    if (!individualForm.account_id || !individualForm.region) {
      setError('Account ID and Region are required to revoke access');
      return;
    }

    const module = individualForm.module.trim();
    const scope = module ? `the "${module}" module` : 'ALL modules';
    if (!window.confirm(`Revoke SAR access to ${scope} for account ${individualForm.account_id.trim()}?`)) {
      return;
    }

    try {
      setLoading(true);
      clearMessages();

      const request: SarRevokeRequest = {
        account_id: individualForm.account_id.trim(),
        region: individualForm.region.trim(),
      };

      const response = await revokeSarAccess(request, module || undefined);

      if (response.data.status === 'success') {
        setMessage(`SAR access revoked (${scope}) for account ${request.account_id}`);
        setIndividualForm({ account_id: '', email: '', region: '', module: '' });
      } else {
        setError(response.data.description || 'Failed to revoke SAR access');
      }
    } catch (err: any) {
      const errorMsg = typeof err.response?.data?.description === 'string'
        ? err.response.data.description
        : 'Failed to revoke SAR access';
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAllAccessSubmit = async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      clearMessages();

      const request: SarAllAccessRequest = {
        all_customers: true,
      };

      const response = await addSarAllAccess(request, true);

      if (response.data.status === 'success') {
        setMessage('SAR all access granted successfully to all customers');
      } else {
        setError(response.data.description || 'Failed to grant all access');
      }
    } catch (err: any) {
      const errorMsg = typeof err.response?.data?.description === 'string'
        ? err.response.data.description
        : 'Failed to grant all access';
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAuthenticated || !notifyForm.release_version) {
      setError('Please fill in the release version');
      return;
    }

    if (notifyForm.email_list === 'custom' && !notifyForm.custom_email.trim()) {
      setError('Please enter a custom email address');
      return;
    }

    if (!notifyForm.send_to_private && !notifyForm.send_to_public) {
      setError('Please select at least one endpoint (Private or Public)');
      return;
    }

    try {
      setLoading(true);
      clearMessages();

      const request: SarNotifyRequest = {
        release_version: notifyForm.release_version.trim(),
        deployment_manual_url: notifyForm.deployment_manual_url.trim(),
        release_notes_url: notifyForm.release_notes_url.trim(),
        email_list: notifyForm.email_list === 'all' ? ['all'] :
                   notifyForm.email_list === 'custom' ? [notifyForm.custom_email.trim()] :
                   notifyForm.email_list.split(',').map(e => e.trim()),
      };

      const response = await sarNotify(request, notifyForm.send_to_private, notifyForm.send_to_public);

      if (response.success) {
        setMessage(response.message);
        setNotifyForm({ ...notifyForm, release_version: '', custom_email: '' });
      } else {
        setError(response.message);
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to send notification';
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleListSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAuthenticated || !listForm.account_id) {
      setError('Please enter an account ID');
      return;
    }

    try {
      setLoading(true);
      clearMessages();

      const response = await getSarList(listForm.account_id.trim());
      // Handle both string and object responses
      const dataToDisplay = typeof response.data === 'string'
        ? response.data
        : JSON.stringify(response.data, null, 2);
      setSarListData(dataToDisplay);
      setMessage('SAR list retrieved successfully');
    } catch (err: any) {
      const errorMsg = typeof err.response?.data?.description === 'string'
        ? err.response.data.description
        : 'Failed to retrieve SAR list';
      setError(errorMsg);
      setSarListData('');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sar-container">
      <header className="sar-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <h1>SAR Management</h1>
      </header>

      <div className="sar-tabs">
        <button
          className={`tab-button ${activeTab === 'individual' ? 'active' : ''}`}
          onClick={() => { setActiveTab('individual'); clearMessages(); }}
        >
          Individual Access
        </button>
        <button
          className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => { setActiveTab('all'); clearMessages(); }}
        >
          All Access
        </button>
        <button
          className={`tab-button ${activeTab === 'notify' ? 'active' : ''}`}
          onClick={() => { setActiveTab('notify'); clearMessages(); }}
        >
          Notifications
        </button>
        <button
          className={`tab-button ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => { setActiveTab('list'); clearMessages(); }}
        >
          View List
        </button>
      </div>

      <div className="sar-content">
        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}

        {activeTab === 'individual' && (
          <div className="tab-content">
            <h2>Grant Individual SAR Access</h2>
            <p className="tab-description">
              Grant Skills Access Request privileges to a specific customer account.
            </p>

            <form onSubmit={handleIndividualSubmit} className="sar-form">
              <div className="form-group">
                <label htmlFor="account_id">Account ID *</label>
                <input
                  id="account_id"
                  type="text"
                  value={individualForm.account_id}
                  onChange={(e) => setIndividualForm({...individualForm, account_id: e.target.value})}
                  placeholder="Enter customer account ID"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  type="email"
                  value={individualForm.email}
                  onChange={(e) => setIndividualForm({...individualForm, email: e.target.value.replace(/\s/g, '')})}
                  placeholder="Enter customer email"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="region">Region *</label>
                <RegionCombobox
                  id="region"
                  value={individualForm.region}
                  onChange={(v) => setIndividualForm({...individualForm, region: v})}
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="module">Module (Optional)</label>
                <input
                  id="module"
                  type="text"
                  value={individualForm.module}
                  onChange={(e) => setIndividualForm({...individualForm, module: e.target.value})}
                  placeholder="Enter specific module name"
                  disabled={loading}
                />
                <p className="field-hint">
                  Grant/Revoke act on this module if set; leave blank to apply to all modules.
                  Revoke only needs Account ID and Region (email is ignored).
                </p>
              </div>

              <div className="sar-form-actions">
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Granting Access...' : 'Grant Access'}
                </button>
                <button
                  type="button"
                  className="revoke-button"
                  onClick={handleIndividualRevoke}
                  disabled={loading}
                >
                  {loading ? 'Working...' : 'Revoke Access'}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'all' && (
          <div className="tab-content">
            <h2>Grant All Customers Access</h2>
            <p className="tab-description">
              Grant SAR access to all customers. This is a powerful operation that affects all users.
            </p>

            <div className="warning-box">
              <strong>⚠️ Warning:</strong> This action will grant SAR access to ALL customers.
              Make sure you understand the implications before proceeding.
            </div>

            <button
              onClick={handleAllAccessSubmit}
              className="submit-button danger-button"
              disabled={loading}
            >
              {loading ? 'Granting Access...' : 'Grant All Access'}
            </button>
          </div>
        )}

        {activeTab === 'notify' && (
          <div className="tab-content">
            <h2>Send Release Notification</h2>
            <p className="tab-description">
              Send release notifications to customers about new deployments and updates.
            </p>

            <form onSubmit={handleNotifySubmit} className="sar-form">
              <div className="form-group">
                <label htmlFor="release_version">Release Version *</label>
                <input
                  id="release_version"
                  type="text"
                  value={notifyForm.release_version}
                  onChange={(e) => setNotifyForm({...notifyForm, release_version: e.target.value})}
                  placeholder="e.g., 2.1.0"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="deployment_manual_url">Deployment Manual URL</label>
                <input
                  id="deployment_manual_url"
                  type="url"
                  value={notifyForm.deployment_manual_url}
                  onChange={(e) => setNotifyForm({...notifyForm, deployment_manual_url: e.target.value})}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="release_notes_url">Release Notes URL</label>
                <input
                  id="release_notes_url"
                  type="url"
                  value={notifyForm.release_notes_url}
                  onChange={(e) => setNotifyForm({...notifyForm, release_notes_url: e.target.value})}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email_list">Email Recipients</label>
                <select
                  id="email_list"
                  value={notifyForm.email_list}
                  onChange={(e) => setNotifyForm({...notifyForm, email_list: e.target.value})}
                  disabled={loading}
                >
                  <option value="all">All customers</option>
                  <option value="custom">Custom email address</option>
                </select>
                <small className="form-help">
                  Choose between all customers or a specific email for testing
                </small>
              </div>

              {notifyForm.email_list === 'custom' && (
                <div className="form-group">
                  <label htmlFor="custom_email">Custom Email Address *</label>
                  <input
                    id="custom_email"
                    type="email"
                    value={notifyForm.custom_email}
                    onChange={(e) => setNotifyForm({...notifyForm, custom_email: e.target.value})}
                    placeholder="Enter email address for testing"
                    disabled={loading}
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label>Notification Endpoints</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={notifyForm.send_to_private}
                      onChange={(e) => setNotifyForm({...notifyForm, send_to_private: e.target.checked})}
                      disabled={loading}
                    />
                    Private (Customer API)
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={notifyForm.send_to_public}
                      onChange={(e) => setNotifyForm({...notifyForm, send_to_public: e.target.checked})}
                      disabled={loading}
                    />
                    Public (api.rainmaker.espressif.com)
                  </label>
                </div>
                <small className="form-help">
                  Select which endpoints to send the notification to. You can select both.
                </small>
              </div>

              <button type="submit" className="submit-button" disabled={loading}>
                {loading ? 'Sending...' : 'Send Notification'}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'list' && (
          <div className="tab-content">
            <h2>View SAR List</h2>
            <p className="tab-description">
              Retrieve the SAR access list for a specific customer account.
            </p>

            <form onSubmit={handleListSubmit} className="sar-form">
              <div className="form-group">
                <label htmlFor="list_account_id">Account ID *</label>
                <input
                  id="list_account_id"
                  type="text"
                  value={listForm.account_id}
                  onChange={(e) => setListForm({...listForm, account_id: e.target.value})}
                  placeholder="Enter customer account ID"
                  disabled={loading}
                  required
                />
              </div>

              <button type="submit" className="submit-button" disabled={loading}>
                {loading ? 'Retrieving...' : 'Get SAR List'}
              </button>
            </form>

            {sarListData && (
              <div className="sar-list-result">
                <h3>SAR List Result</h3>
                <pre className="list-data">{sarListData}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SarScreen;